import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { useActiveWorkspace } from "@/contexts/ActiveWorkspace";
import { useCapture } from "@/contexts/CaptureContext";
import { trpc, type RouterOutputs } from "@/lib/trpc";
import { weekStartUTC, weekKey } from "@shared/workweek";
import { initialWeekOffsetFromUrl } from "@/lib/weekParam";
import type { ReceiptContent } from "@shared/receipt";
import type { DayStatus } from "@shared/trustLabels";
import { TabScaffold, mergeTabQueries } from "@/components/tabs/TabScaffold";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AppTopBar } from "@/components/home/AppTopBar";
import { DraftReceiptCard } from "@/components/drafts/DraftReceiptCard";
import { resolveReceiptFor } from "@/components/drafts/resolveReceipt";
import { DailyReceiptsList } from "@/components/drafts/DailyReceiptsList";
import { ReceiptHistoryList } from "@/components/drafts/ReceiptHistoryList";
import { ApprovePreviewSheet } from "@/components/drafts/ApprovePreviewSheet";
import { ReceiptEditor } from "@/components/ReceiptEditor";
import { draftsIsEmpty } from "@/lib/draftsEmptyState";
import { showTodayDraftCard, showEditorUnshare, weeklyCardStatus } from "@/lib/draftsLayout";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

const NO_ACTIVITY_MSG = (isAr: boolean) =>
  isAr
    ? "لم يُلتقط أي نشاط بعد. التقط تحديثاً أولاً، ثم أنشئ مسودة ملخّصك."
    : "No activity captured yet. Capture an update first, then draft your receipt.";
const NO_WEEKLY_ACTIVITY_MSG = (isAr: boolean) =>
  isAr
    ? "لا نشاط أسبوعي بعد. التقط تحديثاً أولاً، ثم أنشئ مسودة ملخّصك الأسبوعي."
    : "No weekly activity yet. Capture an update first, then draft your weekly receipt.";
const DRAFT_ERROR_MSG = (isAr: boolean) =>
  isAr ? "تعذّر إنشاء المسودة. حاول مرة أخرى." : "Couldn’t create the draft. Please try again.";
const OPEN_DRAFT_ERROR_MSG = (isAr: boolean) =>
  isAr
    ? "تعذّر فتح هذه المسودة. قد تكون لا تزال قيد المزامنة. حاول مرة أخرى."
    : "Couldn’t open this draft. It may still be syncing. Please try again.";

const NOW = new Date();

type ForWeek = RouterOutputs["receipts"]["forWeek"];
type AnyReceipt = ForWeek["daily"][number];

/**
 * Drafts tab — the owner's private review → edit → approve & share surface.
 * Thin client: every status comes from the server (`receipts.weekStatus` for
 * per-day status + the workday denominator; `receipts.forWeek` for content +
 * the server `weeklyStale` flag). The approval screen renders the EXACT server
 * projection from `receipts.previewApproval` (A3). The client computes NO
 * approval state, NO stale state, NO day classification, NO denominator, and
 * NO feed eligibility. Editing reuses the existing `ReceiptEditor`.
 */
export default function Drafts() {
  const { isAuthenticated } = useAuth();
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const [, navigate] = useLocation();
  const ws = useActiveWorkspace();
  const teamId = ws.active?.team.id ?? null;
  // Personal workspace → drop team-sharing language on the draft cards + the
  // approval preview (shareability is a property of the receipt's workspace).
  const wsIsPersonal = ws.active?.team.isPersonal ?? false;
  const { openCapture } = useCapture();
  // Open on the week from a `?d=`/`?week=` deep link (e.g. a tapped day in the
  // Home week strip); defaults to the current week.
  const [weekOffset, setWeekOffset] = useState(initialWeekOffsetFromUrl);

  const prefs = trpc.preferences.me.useQuery(undefined, { enabled: isAuthenticated });
  const weekStartDay = prefs.data?.weekStartDay ?? 0;
  const weekStart = useMemo(() => {
    const b = weekStartUTC(NOW, weekStartDay);
    b.setUTCDate(b.getUTCDate() + weekOffset * 7);
    return b;
  }, [weekStartDay, weekOffset]);
  const weekStartKey = useMemo(() => weekKey(weekStart, weekStartDay), [weekStart, weekStartDay]);
  const todayKey = new Date().toLocaleDateString("en-CA"); // local YYYY-MM-DD

  const enabled = isAuthenticated && teamId != null;
  const forWeek = trpc.receipts.forWeek.useQuery({ weekStartKey, workspaceTeamId: teamId }, { enabled });
  const weekStatus = trpc.receipts.weekStatus.useQuery({ weekStartKey, workspaceTeamId: teamId }, { enabled });
  // Raw private captures for the viewed week — shown read-only inside the editor
  // so nothing the user captured is invisible during review (the receipt is a
  // SUMMARY). Owner-only; never leaves this screen.
  const weekSignals = trpc.signals.forWeek.useQuery(
    { weekStartKey, workspaceTeamId: teamId },
    { enabled },
  );
  // Scope the Receipt-history strip to the active workspace, like every other
  // read on this page — otherwise it shows approved weeklies from ALL the
  // user's workspaces and doesn't change when they switch the workspace.
  const history = trpc.profile.approvedWeeklies.useQuery(
    { workspaceTeamId: teamId },
    { enabled },
  );

  const utils = trpc.useUtils();
  const invalidate = () => {
    utils.receipts.forWeek.invalidate();
    utils.yourWeek.forWeek.invalidate();
    utils.receipts.weekStatus.invalidate();
    utils.profile.approvedWeeklies.invalidate();
  };
  const draftDaily = trpc.receipts.draftDaily.useMutation({ onSuccess: invalidate });
  const draftWeekly = trpc.receipts.draftWeekly.useMutation({ onSuccess: invalidate });
  // Edit / delete a raw capture from inside the editor's "Captured this …" panel.
  const invalidateCaptures = () => {
    utils.signals.forWeek.invalidate();
    utils.receipts.weekStatus.invalidate();
    utils.receipts.forWeek.invalidate();
    utils.yourWeek.forWeek.invalidate();
  };
  const updateSignal = trpc.signals.update.useMutation({ onSuccess: invalidateCaptures });
  const deleteSignal = trpc.signals.delete.useMutation({ onSuccess: invalidateCaptures });
  const editMut = trpc.receipts.edit.useMutation({ onSuccess: invalidate });
  const approveMut = trpc.receipts.approve.useMutation({ onSuccess: invalidate });
  // Silent owner takedown: removes the receipt from the Team feed (snapshot +
  // audit retained server-side). No teammate notification by design.
  const retractMut = trpc.receipts.retract.useMutation({
    onSuccess: () => {
      invalidate();
      utils.team.sharedReceipts.invalidate();
      toast.success(
        isAr
          ? "أُلغيت المشاركة. فريقك لم يعد يرى هذا الملخّص."
          : "Unshared. Your team no longer sees this receipt.",
      );
    },
    onError: () =>
      toast.error(isAr ? "تعذّر إلغاء مشاركة هذا الملخّص." : "Couldn’t unshare this receipt."),
  });

  // Sheets / dialogs (layered over the success view).
  const [editing, setEditing] = useState<AnyReceipt | null>(null);
  const [approving, setApproving] = useState<{ id: number; weekly: boolean } | null>(null);
  // Retract ("Unshare") confirm target — explicit two-step takedown.
  const [unsharing, setUnsharing] = useState<AnyReceipt | null>(null);
  // Bumped on an explicit in-editor "Regenerate with AI" so the editor remounts
  // with the fresh AI content (a regenerate is meant to REPLACE local edits).
  const [regenNonce, setRegenNonce] = useState(0);

  // Deep-link review: Home's Receipt Actions navigates here with
  // `?review=weekly` or `?review=daily&d=<date>` to open the matching draft's
  // review/edit flow directly (the editor → Approve goes through the approval
  // preview). Consumed once, after the week's data loads.
  // REGRESSION FIX: this used to consume the param on the FIRST render where
  // forWeek.data existed — which could be the STALE cached payload that does
  // not yet contain the just-drafted receipt (e.g. Home cached the week, the
  // user drafted, then deep-linked here). The target wasn't found, the param
  // was consumed, and the editor silently never opened. Now it routes through
  // `openReceipt`, which falls back to a FRESH fetch and toasts on failure.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const review = sp.get("review");
    if (!review) return;
    if (!enabled) return; // wait for auth + workspace so the fetch has its inputs
    autoOpenedRef.current = true;
    if (review === "weekly") {
      void openReceipt("weekly", undefined, null, "review");
    } else if (review === "daily") {
      const dParam = sp.get("d");
      if (dParam) void openReceipt("daily", dParam, null, "review");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const state = mergeTabQueries({ forWeek, weekStatus, history });

  const weekLabel = useMemo(() => {
    const end = new Date(weekStart);
    end.setUTCDate(weekStart.getUTCDate() + 6);
    const f = (d: Date) =>
      d.toLocaleDateString("en", { month: "short", day: "numeric", timeZone: "UTC" });
    return `${f(weekStart)} — ${f(end)}`;
  }, [weekStart]);

  // Truthful week-context label for whichever week is in view (presentation only).
  const weekContextLabel = useMemo(() => {
    if (weekOffset === 0) return isAr ? "هذا الأسبوع" : "This week";
    if (weekOffset === -1) return isAr ? "الأسبوع الماضي" : "Last week";
    if (weekOffset === 1) return isAr ? "الأسبوع القادم" : "Next week";
    const f = weekStart.toLocaleDateString("en", {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
    return isAr ? `أسبوع ${f}` : `Week of ${f}`;
  }, [weekOffset, weekStart, isAr]);
  const isCurrentWeek = weekOffset === 0;

  const weekNav = (
    <div className="flex items-center gap-2">
      <button onClick={() => setWeekOffset(o => o - 1)} aria-label={isAr ? "الأسبوع السابق" : "Previous week"} className="rounded-full p-1 hover:bg-muted">
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="tabular-nums">{weekLabel}</span>
      <button onClick={() => setWeekOffset(o => o + 1)} aria-label={isAr ? "الأسبوع التالي" : "Next week"} className="rounded-full p-1 hover:bg-muted">
        <ChevronRight className="h-4 w-4" />
      </button>
      {!isCurrentWeek ? (
        <button
          onClick={() => setWeekOffset(0)}
          className="ms-1 inline-flex items-center gap-1 rounded-full bg-coral-soft/60 text-coral px-2.5 py-0.5 text-xs font-medium hover:bg-coral-soft"
        >
          <RotateCcw className="h-3 w-3" /> {isAr ? "هذا الأسبوع" : "This week"}
        </button>
      ) : null}
    </div>
  );

  // Always edit the FRESHEST copy of the receipt. Two stale-content traps:
  //   1) the click-time snapshot goes stale while the `forWeek` query refreshes
  //      (card moves ahead of the editor), and
  //   2) "Review updates" regenerates and hands us a FRESH receipt via
  //      setEditing(), but `forWeek` hasn't refetched yet — so naively
  //      preferring the query would show the OLD pre-capture content.
  // Resolve by `updatedAt`: whichever of {snapshot, query-match} is newer wins.
  const liveEditing: AnyReceipt | null = (() => {
    if (!editing) return null;
    const data = forWeek.data;
    if (!data) return editing;
    const id = (editing as any).id;
    const match =
      (editing as any).kind === "weekly"
        ? data.weekly && (data.weekly as any).id === id
          ? (data.weekly as AnyReceipt)
          : null
        : ((data.daily as any[]).find(r => r.id === id) as AnyReceipt | undefined) ?? null;
    if (!match) return editing;
    const t = (r: any) => (r?.updatedAt ? new Date(r.updatedAt).getTime() : 0);
    return t(match) >= t(editing) ? match : editing;
  })();

  // Captures shown inside the editor for the receipt being edited: a daily
  // receipt shows ITS day's captures; a weekly shows the whole week's.
  const isWeeklyEdit = (liveEditing as any)?.kind === "weekly";
  const editPeriodKey = (liveEditing as any)?.periodKey as string | undefined;
  const editorCaptures = (() => {
    if (!liveEditing) return [] as { id: string | number; text: string }[];
    const sigs = (weekSignals.data as any[]) ?? [];
    return sigs
      .filter(s => {
        if (isWeeklyEdit) return true; // weekly → all week's captures
        if (!editPeriodKey) return false;
        const occ = s.occurredAt ? new Date(s.occurredAt).toISOString().slice(0, 10) : null;
        return occ === editPeriodKey; // daily → that day's captures
      })
      .map(s => ({ id: s.id, text: (s.title ?? s.summary ?? "").trim() }))
      .filter(c => c.text.length > 0);
  })();

  const openApprove = (id: number, weekly: boolean) => setApproving({ id, weekly });

  // REGRESSION FIX (Review/Edit no-op): the action buttons used to be wired
  // as `() => todayDaily && setEditing(todayDaily)` — when the card's STATUS
  // (weekStatus query) said "reviewable" but the matching ROW was missing
  // from the forWeek cache (refetch race right after capture→draft, or a
  // stale cache when arriving via Home's deep link), the tap did NOTHING.
  // `openReceipt` resolves snapshot → cache → FRESH FETCH, shows a busy
  // state while resolving, and surfaces a toast instead of going silent.
  const [resolving, setResolving] = useState<null | "daily" | "weekly">(null);
  const openReceipt = async (
    kind: "daily" | "weekly",
    periodKey: string | undefined,
    snapshot: AnyReceipt | null | undefined,
    action: "review" | "approve" | "unshare",
  ) => {
    if (resolving) return; // one resolution at a time
    setResolving(kind);
    try {
      const res = await resolveReceiptFor({
        kind,
        periodKey,
        snapshot: snapshot as any,
        cached: forWeek.data as any,
        fetchFresh: () =>
          utils.receipts.forWeek.fetch({ weekStartKey, workspaceTeamId: teamId }) as any,
      });
      if (!res.ok) {
        toast.error(OPEN_DRAFT_ERROR_MSG(isAr));
        return;
      }
      const receipt = res.receipt as unknown as AnyReceipt;
      if (action === "review") setEditing(receipt);
      else if (action === "approve") requestApprove(receipt, kind === "weekly");
      else setUnsharing(receipt);
    } finally {
      setResolving(null);
    }
  };

  // A receipt has an UNINCORPORATED capture when the server flags it stale —
  // i.e. a signal was captured after the content was last drafted/edited
  // (`dailyStaleByKey` / `weeklyStale`). In that case we never let the user
  // approve straight from the card: route through the editor FIRST so the new
  // capture (shown in the "Captured this …" panel) is seen before publishing.
  const hasUnincorporatedCapture = (receipt: any): boolean => {
    const data = forWeek.data;
    if (!data || !receipt) return false;
    if (receipt.kind === "weekly") return !!(data as any).weeklyStale;
    return !!(data as any).dailyStaleByKey?.[receipt.periodKey];
  };
  const requestApprove = (receipt: any, weekly: boolean) => {
    if (!receipt) {
      // Never a silent no-op — the resolver normally prevents this, but if a
      // caller still hands us nothing, say so.
      toast.error(OPEN_DRAFT_ERROR_MSG(isAr));
      return;
    }
    if (hasUnincorporatedCapture(receipt)) {
      setEditing(receipt as AnyReceipt);
      toast.info(
        isAr
          ? "التقطت شيئاً جديداً. راجعه هنا، ثم اعتمد."
          : "You captured something new. Review it here, then approve.",
      );
      return;
    }
    openApprove(receipt.id, weekly);
  };
  const confirmApprove = (shareDetailLevel: "headline" | "highlights" | "full") => {
    if (!approving) return;
    approveMut.mutate(
      {
        receiptId: approving.id,
        shareDetailLevel,
        ...(approving.weekly ? { weekStartKey } : {}),
      },
      { onSuccess: () => setApproving(null) },
    );
  };

  return (
    <>
      <TabScaffold
        title={isAr ? "المسودات" : "Drafts"}
        subtitle={weekNav}
        topBar={<AppTopBar />}
        state={state}
        // "Nothing to review" ONLY when there is genuinely nothing to act on:
        // no weekly, no daily receipts, no history — AND no captured signals
        // this week. Without the signal check, a week with captures but no
        // drafted receipt yet (e.g. a rest-day/weekend note that feeds the
        // weekly) showed the empty state and HID the "Draft now" card, so the
        // user couldn't turn their capture into a draft from here.
        isEmpty={d =>
          draftsIsEmpty({
            hasWeekly: !!d.forWeek.weekly,
            dailyReceiptCount: d.forWeek.daily.length,
            historyCount: d.history.length,
            daySignalCounts: d.weekStatus.days.map(x => x.signalCount),
          })
        }
        emptyTitle={isAr ? "لا شيء للمراجعة بعد" : "Nothing to review yet"}
        emptyHint={
          isAr
            ? "التقط ملاحظة أو تسجيلاً صوتياً أو صورة. يتحوّل إلى مسودة يمكنك مراجعتها واعتمادها ومشاركتها."
            : "Capture a note, voice memo or photo. It turns into a draft you can review, approve & share."
        }
      >
        {d => {
          // Today (server day status) + this-week (server weekly status + flag).
          const todayCell = d.weekStatus.days.find(x => x.dateKey === todayKey);
          const todayDaily = d.forWeek.daily.find((r: any) => r.periodKey === todayKey);
          const todayStatus = (todayCell?.status ?? "not_started") as DayStatus;
          // Whether today is already a row in the Daily Receipts list below
          // (working day, or a rest day carrying a captured note) — mirrors
          // DailyReceiptsList's own row filter. Used to drop the duplicate
          // standalone Today card in a PERSONAL workspace only.
          const todayInDailyList =
            !!todayCell && (todayCell.isWorkingDay || todayCell.status === "weekend_note");

          const weekly = d.forWeek.weekly as any;
          // Presentation selection driven ENTIRELY by server values (weekly
          // status enum + the server `weeklyStale` flag) via the pure, tested
          // `weeklyCardStatus`. NOTE: "Changes not shared"/Unshare require a
          // prior approval — a never-approved-but-stale weekly is just "ready"
          // (see weeklyCardStatus for the "Couldn't unshare" bug it fixes).
          const weekStatusDisplay: DayStatus = weeklyCardStatus(
            weekly,
            d.forWeek.weeklyStale,
          );

          const dailyByKey: Record<string, AnyReceipt> = {};
          for (const r of d.forWeek.daily) dailyByKey[(r as any).periodKey] = r;

          const todayDate = new Date(todayKey + "T00:00:00.000Z").toLocaleDateString("en", {
            weekday: "long",
            month: "short",
            day: "numeric",
            timeZone: "UTC",
          });

          // Activity gates (server-provided signal counts; no client classification).
          const todayHasActivity = (todayCell?.signalCount ?? 0) > 0;
          const weekHasActivity =
            d.weekStatus.days.some(x => x.signalCount > 0) || d.forWeek.daily.length > 0;

          // Guarded draft actions: explain when there's nothing to draft; on
          // success open the editor (review); on failure toast. Never silent.
          const draftDay = (dateKey: string, hasActivity: boolean) => {
            if (!hasActivity)
              return toast.error(NO_ACTIVITY_MSG(isAr), {
                action: { label: isAr ? "التقاط تحديث" : "Capture update", onClick: () => openCapture(dateKey) },
              });
            draftDaily.mutate(
              { date: dateKey, locale: "en", workspaceTeamId: teamId },
              {
                onSuccess: (rec: any) => rec && setEditing(rec as AnyReceipt),
                onError: () => toast.error(DRAFT_ERROR_MSG(isAr)),
              },
            );
          };
          const draftWeek = () => {
            if (!weekHasActivity) {
              // Open Capture preselected on the week being VIEWED (first
              // working day), not blindly on today/current week.
              const captureDay = isCurrentWeek
                ? undefined
                : (d.weekStatus.days.find(x => x.isWorkingDay)?.dateKey ?? weekStartKey);
              return toast.error(NO_WEEKLY_ACTIVITY_MSG(isAr), {
                action: { label: isAr ? "التقاط تحديث" : "Capture update", onClick: () => openCapture(captureDay) },
              });
            }
            draftWeekly.mutate(
              { weekStartKey, locale: "en", workspaceTeamId: teamId },
              {
                onSuccess: (rec: any) => rec && setEditing(rec as AnyReceipt),
                onError: () => toast.error(DRAFT_ERROR_MSG(isAr)),
              },
            );
          };

          return (
            <>
              {/* 1) TODAY — only on the current week (it's about today). In a
                  PERSONAL workspace it's hidden when today is already a row in
                  the Daily Receipts list below, to avoid showing the same day
                  twice. Team behaviour is unchanged. */}
              {showTodayDraftCard({ isCurrentWeek, isPersonal: wsIsPersonal, todayInDailyList }) ? (
                <DraftReceiptCard
                  label={isAr ? "اليوم" : "Today"}
                  dateLabel={todayDate}
                  status={todayStatus}
                  headline={(todayDaily as any)?.content?.headline}
                  capturedCount={todayCell?.signalCount ?? 0}
                  busy={draftDaily.isPending || resolving === "daily"}
                  onDraft={() => draftDay(todayKey, todayHasActivity)}
                  onReview={() => void openReceipt("daily", todayKey, todayDaily, "review")}
                  onApprove={() => void openReceipt("daily", todayKey, todayDaily, "approve")}
                  onUnshare={() => void openReceipt("daily", todayKey, todayDaily, "unshare")}
                  isPersonal={wsIsPersonal}
                />
              ) : null}

              {/* 2) THIS WEEK */}
              <DraftReceiptCard
                label={weekContextLabel}
                dateLabel={weekLabel}
                status={weekStatusDisplay}
                headline={weekly?.content?.headline}
                capturedCount={d.weekStatus.days.reduce((a, x) => a + x.signalCount, 0)}
                busy={draftWeekly.isPending || resolving === "weekly"}
                onDraft={() => draftWeek()}
                onReview={() => void openReceipt("weekly", undefined, weekly, "review")}
                onApprove={() => void openReceipt("weekly", undefined, weekly, "approve")}
                onUnshare={() => void openReceipt("weekly", undefined, weekly, "unshare")}
                isPersonal={wsIsPersonal}
              />

              {/* 3) DAILY RECEIPTS */}
              <DailyReceiptsList
                weekStatus={d.weekStatus}
                dailyByKey={dailyByKey}
                isPersonal={wsIsPersonal}
                busyKey={draftDaily.isPending ? (draftDaily.variables as any)?.date : null}
                onDraft={dateKey => {
                  const cell = d.weekStatus.days.find(x => x.dateKey === dateKey);
                  draftDay(dateKey, (cell?.signalCount ?? 0) > 0);
                }}
                onReview={r => setEditing(r)}
                onApprove={r => requestApprove(r, false)}
              />

              {/* 4) RECEIPT HISTORY */}
              <ReceiptHistoryList
                weeklies={d.history}
                onOpenWeek={periodKey => {
                  // Jump this tab to the tapped week. periodKey is that week's
                  // start key, aligned to the same weekStartDay — so the offset
                  // from the CURRENT week's start is an exact multiple of 7d.
                  const base = weekStartUTC(NOW, weekStartDay).getTime();
                  const target = new Date(periodKey + "T00:00:00.000Z").getTime();
                  setWeekOffset(Math.round((target - base) / (7 * 86_400_000)));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </>
          );
        }}
      </TabScaffold>

      {/* Edit step — reuses the existing ReceiptEditor (no duplicate logic). */}
      <Dialog open={editing != null} onOpenChange={o => !o && setEditing(null)}>
        <DialogContent
          data-testid="edit-receipt"
          className="max-h-[90dvh] overflow-y-auto max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-full max-sm:max-w-none max-sm:rounded-b-none max-sm:rounded-t-2xl"
        >
          <DialogHeader>
            <DialogTitle>{isAr ? "تعديل الملخّص" : "Edit receipt"}</DialogTitle>
          </DialogHeader>
          {liveEditing ? (
            <ReceiptEditor
              // Re-key on receipt id (and the regenerate nonce) so switching
              // receipts — or an explicit AI regenerate — remounts the editor
              // with fresh content (no stale carry-over). The nonce only bumps on
              // regenerate, NOT on autosave, so typing never remounts.
              key={`${(liveEditing as any).id}:${regenNonce}`}
              initial={(liveEditing as any).content as ReceiptContent}
              initialUpdatedAt={(liveEditing as any).updatedAt ? new Date((liveEditing as any).updatedAt) : null}
              onPersist={async (next, expectedUpdatedAt) => {
                const res = await editMut.mutateAsync({
                  receiptId: (liveEditing as any).id,
                  content: next,
                  ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}),
                });
                return { updatedAt: res.updatedAt ? new Date(res.updatedAt) : null };
              }}
              // The editor renders its own "Approve & share" CTA (it flushes
              // pending autosaves first, then opens the approval preview).
              // Personal workspace → no team audience, so the editor CTA drops
              // "& share" (matches the rest of the personal sharing-language).
              approveLabel={
                (liveEditing as any).approvedAt
                  ? wsIsPersonal
                    ? isAr ? "إعادة الاعتماد" : "Re-approve"
                    : isAr ? "إعادة الاعتماد والمشاركة" : "Re-approve & share"
                  : wsIsPersonal
                    ? isAr ? "اعتماد" : "Approve"
                    : isAr ? "اعتماد ومشاركة" : "Approve & share"
              }
              captures={editorCaptures}
              capturesPeriodLabel={isWeeklyEdit ? (isAr ? "الأسبوع" : "week") : (isAr ? "اليوم" : "day")}
              onEditCapture={(id, text) => updateSignal.mutate({ signalId: Number(id), title: text })}
              onDeleteCapture={id => deleteSignal.mutate({ signalId: Number(id) })}
              regenerating={draftDaily.isPending || draftWeekly.isPending}
              onRegenerate={() => {
                const onSuccess = (rec: any) => {
                  if (rec) setEditing(rec as AnyReceipt);
                  setRegenNonce(n => n + 1); // force the editor to remount with the AI draft
                  toast.success(
                    isAr
                      ? "أُعيد إنشاء الملخّص من أحدث تحديثاتك."
                      : "Receipt regenerated from your latest captures.",
                  );
                };
                const onError = () => toast.error(DRAFT_ERROR_MSG(isAr));
                if (isWeeklyEdit) {
                  draftWeekly.mutate({ weekStartKey, locale: "en", workspaceTeamId: teamId }, { onSuccess, onError });
                } else if (editPeriodKey) {
                  draftDaily.mutate({ date: editPeriodKey, locale: "en", workspaceTeamId: teamId }, { onSuccess, onError });
                }
              }}
              onApprove={() => {
                const id = (liveEditing as any).id as number;
                const weekly = (liveEditing as any).kind === "weekly";
                setEditing(null);
                openApprove(id, weekly);
              }}
            />
          ) : null}

          {liveEditing ? (
            <DialogFooter className="mt-4 gap-2 border-t border-border/60 pt-3">
              {/* Unshare = retract from the TEAM feed — only meaningful in a
                  team workspace. A personal receipt is never shared, so there's
                  nothing to unshare (matches the personal sharing-language
                  boundary already applied to the draft cards). */}
              {showEditorUnshare({
                isPersonal: wsIsPersonal,
                approvedAt: (liveEditing as any).approvedAt,
                retractedAt: (liveEditing as any).retractedAt,
              }) ? (
                <Button
                  variant="ghost"
                  onClick={() => setUnsharing(liveEditing)}
                  className="rounded-full text-muted-foreground hover:text-destructive max-sm:w-full"
                  data-testid="editor-unshare"
                >
                  {isAr ? "إلغاء المشاركة" : "Unshare"}
                </Button>
              ) : null}
              <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full max-sm:w-full">
                {isAr ? "تم" : "Done"}
              </Button>
            </DialogFooter>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Unshare confirm — explicit takedown; forward-looking only. */}
      <Dialog open={unsharing != null} onOpenChange={o => !o && setUnsharing(null)}>
        <DialogContent data-testid="unshare-confirm" className="max-w-sm max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-full max-sm:max-w-none max-sm:rounded-b-none max-sm:rounded-t-2xl">
          <DialogHeader>
            <DialogTitle>{isAr ? "إزالته من خلاصة فريقك؟" : "Remove from your team’s feed?"}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {isAr ? (
              "لن يرى فريقك هذا الملخّص بعد الآن. قد يكون زملاؤك شاهدوه بالفعل. يمكنك مشاركته مجدداً لاحقاً عبر إعادة الاعتماد."
            ) : (
              <>
                Your team will no longer see this receipt. Teammates may have
                already seen it. You can share it again later by re-approving.
              </>
            )}
          </p>
          <DialogFooter className="mt-2 gap-2 max-sm:flex-col-reverse">
            <Button variant="outline" onClick={() => setUnsharing(null)} className="rounded-full max-sm:w-full">
              {isAr ? "إلغاء" : "Cancel"}
            </Button>
            <Button
              variant="destructive"
              disabled={retractMut.isPending}
              onClick={() => {
                const id = (unsharing as any)?.id as number | undefined;
                if (id == null) return;
                setEditing(null); // if invoked from the editor, close it too
                retractMut.mutate(
                  { receiptId: id },
                  { onSuccess: () => setUnsharing(null) },
                );
              }}
              className="rounded-full max-sm:w-full"
              data-testid="unshare-confirm-button"
            >
              {retractMut.isPending ? "…" : isAr ? "إلغاء المشاركة" : "Unshare"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve & Share — shows the EXACT server projection before approval. */}
      <ApprovePreviewSheet
        receiptId={approving?.id ?? null}
        open={approving != null}
        onOpenChange={o => !o && setApproving(null)}
        onConfirm={confirmApprove}
        busy={approveMut.isPending}
        isPersonal={wsIsPersonal}
      />
    </>
  );
}
